import { gql } from "@apollo/client";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import { uuid } from "config/app-constants";
import { graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { RoomDatalistQuery, RoomDatalistQueryVariables, RoomFilterInput, RoomSortInput, SortEnumType } from "types/graphql";
import { Roles } from "utilities/app-utilities";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import RoomTypeView from "../roomType/RoomTypeView";
import { faArrowDownWideShort, faHashtag } from "@fortawesome/free-solid-svg-icons";
import { deleteMutation } from "utilities/entities/common/delete-mutation";

type RoomDatalistPropsType = {
    name: string;
}

type DataItem = NonNullable<RoomDatalistQuery['rooms']>['items'][0];

const query = gql`
query RoomDatalist($where: RoomFilterInput, $order: [RoomSortInput!], $skip: Int, $take: Int) {
    rooms {
      count(where: $where)
      items(order: $order, skip: $skip, take: $take, where: $where) {
        id
        name
        roomType {
          color
          name
        }
        order
        bookingDetails {
          any
        }
      }
    }
  }
  `;
const RoomDatalist = (props: RoomDatalistPropsType) => {
    const context = useAppContext();
    const datalistConfig: DatalistPropsType<DataItem, RoomFilterInput, RoomSortInput> = {
        dataSource: {
            request: async params => {
                let variables: RoomDatalistQueryVariables = {
                    where: params.filters,
                    order: params.sort,
                    skip: params.skip,
                    take: params.take
                };

                if (params.searchText) {
                    variables.where = appendFilters(variables.where, {
                        name: {
                            contains: params.searchText
                        },
                        roomType: {
                            name: {
                                contains: params.searchText
                            }
                        }
                    });
                }

                try {
                    const response = await graph<RoomDatalistQuery, RoomDatalistQueryVariables>({
                        query,
                        variables
                    });

                    if (response.errors) {
                        throw response.errors;
                    }

                    return response.data!.rooms!;
                } catch (error: any) {
                    context.notify('error', 'context.load.error', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
            sort: [
                {
                    name: 'name',
                    id: uuid(),
                    direction: SortEnumType.Asc
                }
            ]
        },
        itemId(i) {
            return i.id;
        },
        name: props.name,
        table: {
            columns: [
                {
                    name: 'id',
                    card: {
                        hide: true
                    }
                },
                {
                    name: 'name',
                    filter: {
                        type: 'string'
                    },
                    sort: true,
                    card: {
                        icon: faHashtag,
                        group: "id"
                    }
                },
                {
                    name: 'roomType',
                    resolve: (i) => <RoomTypeView {...i.roomType} />,
                    card: {
                        group: "id"
                    }
                },
                {
                    name: 'order',
                    sort: true,
                    card: {
                        icon: faArrowDownWideShort,
                        group: "id"
                    }
                }
            ],
            render: {
                actions(i) {
                    return {
                        mutationPermissionRole: Roles.ADMIN,
                        editUrl: AppRoutes.ACCOMMODATION_ROOM_EDIT,
                        delete: i.bookingDetails.any ? undefined : (async (item: DataItem, e: React.MouseEvent) => {
                            await deleteMutation('roomDelete', item.id);
                        })
                    }
                }
            }
        }
    };

    return (
        <Datalist {...datalistConfig} />
    );
};

export default RoomDatalist;
